import React, { useState } from 'react';
import ImportExportButtons from '../../../components/common/ImportExportButtons';
import { exportToCSV, downloadCSV, parseCSV, readFileAsText, formatDateForCSV, formatCurrencyForCSV } from '../../../utils/csvUtils';
import api from '../../../utils/apiConfig';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Menu,
  MenuItem,
  TablePagination,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Call as CallIcon,
  MoreVert as MoreIcon,
  Person as PersonIcon,
  Star as StarIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as RevenueIcon,
} from '@mui/icons-material';

const Customers = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const customers = [
    {
      id: 'CUST-001',
      name: 'John Smith',
      email: 'john.smith@email.com',
      phone: '+1 (555) 123-4567',
      company: 'Smith Interiors',
      segment: 'VIP',
      totalOrders: 12,
      totalSpent: 15600,
      averageOrderValue: 1300,
      lastOrderDate: '2024-01-15',
      registrationDate: '2023-08-15',
      status: 'Active',
      notes: 'Prefers modern furniture styles',
      orderHistory: [
        { date: '2024-01-15', amount: 2400, items: 'Modern Sofa Set' },
        { date: '2023-12-10', amount: 890, items: 'Coffee Table' },
        { date: '2023-11-22', amount: 1200, items: 'Dining Chairs (4)' },
      ],
    },
    {
      id: 'CUST-002',
      name: 'Sarah Johnson',
      email: 'sarah@example.com',
      phone: '+1 (555) 987-6543',
      company: 'Johnson Design Co.',
      segment: 'Premium',
      totalOrders: 8,
      totalSpent: 9200,
      averageOrderValue: 1150,
      lastOrderDate: '2024-01-12',
      registrationDate: '2023-09-20',
      status: 'Active',
      notes: 'Interior designer, bulk orders',
      orderHistory: [
        { date: '2024-01-12', amount: 1800, items: 'Oak Dining Table' },
        { date: '2023-12-05', amount: 650, items: 'Floor Lamp' },
        { date: '2023-11-15', amount: 2200, items: 'Bedroom Set' },
      ],
    },
    {
      id: 'CUST-003',
      name: 'Mike Wilson',
      email: 'mike.wilson@company.com',
      phone: '+1 (555) 456-7890',
      company: 'Wilson Corp',
      segment: 'Regular',
      totalOrders: 5,
      totalSpent: 4800,
      averageOrderValue: 960,
      lastOrderDate: '2024-01-08',
      registrationDate: '2023-10-10',
      status: 'Active',
      notes: 'Price-conscious buyer',
      orderHistory: [
        { date: '2024-01-08', amount: 750, items: 'Office Chair' },
        { date: '2023-12-20', amount: 1200, items: 'Desk' },
        { date: '2023-11-30', amount: 450, items: 'Bookshelf' },
      ],
    },
    {
      id: 'CUST-004',
      name: 'Emily Davis',
      email: 'emily.davis@email.com',
      phone: '+1 (555) 321-0987',
      company: 'Davis Home Solutions',
      segment: 'New',
      totalOrders: 2,
      totalSpent: 1800,
      averageOrderValue: 900,
      lastOrderDate: '2024-01-18',
      registrationDate: '2024-01-05',
      status: 'Active',
      notes: 'New customer, potential for growth',
      orderHistory: [
        { date: '2024-01-18', amount: 1200, items: 'Queen Bed Frame' },
        { date: '2024-01-10', amount: 600, items: 'Nightstand Set' },
      ],
    },
  ];

  const stats = [
    {
      title: 'Total Customers',
      value: customers.length.toString(),
      icon: <PersonIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
    },
    {
      title: 'VIP Customers',
      value: customers.filter(c => c.segment === 'VIP').length.toString(),
      icon: <StarIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
    },
    {
      title: 'Total Orders',
      value: customers.reduce((acc, c) => acc + c.totalOrders, 0).toString(),
      icon: <OrdersIcon sx={{ fontSize: 40, color: 'info.main' }} />,
    },
    {
      title: 'Total Revenue',
      value: `$${customers.reduce((acc, c) => acc + c.totalSpent, 0).toLocaleString()}`,
      icon: <RevenueIcon sx={{ fontSize: 40, color: 'success.main' }} />,
    },
  ];

  const segments = ['All', 'VIP', 'Premium', 'Regular', 'New'];

  const getSegmentColor = (segment) => {
    switch (segment) {
      case 'VIP':
        return 'error';
      case 'Premium':
        return 'warning';
      case 'Regular':
        return 'primary';
      case 'New':
        return 'success';
      default:
        return 'default';
    }
  };

  const filteredCustomers = customers.filter((customer) => {
    return (
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.company.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const handleDetailsOpen = (customer) => {
    setSelectedCustomer(customer);
    setDetailsOpen(true);
    setAnchorEl(null);
  };

  const handleMenuOpen = (event, customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  // CSV Export Handler
  const handleExport = () => {
    if (!customers || customers.length === 0) {
      alert('No customers to export');
      return;
    }

    const columns = [
      { field: 'name', header: 'Name' },
      { field: 'email', header: 'Email' },
      { field: 'phone', header: 'Phone' },
      { field: 'company', header: 'Company' },
      { field: 'segment', header: 'Segment' },
      { field: 'totalOrders', header: 'Total Orders' },
      { field: 'totalSpent', header: 'Total Spent' },
      { field: 'lastOrderDate', header: 'Last Order Date' },
      { field: 'tags', header: 'Tags' },
      { field: 'notes', header: 'Notes' }
    ];

    const exportData = customers.map(customer => ({
      ...customer,
      totalSpent: formatCurrencyForCSV(customer.totalSpent),
      lastOrderDate: formatDateForCSV(customer.lastOrderDate),
      tags: Array.isArray(customer.tags) ? customer.tags.join(', ') : customer.tags
    }));

    const csv = exportToCSV(exportData, columns);
    const filename = `customers_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csv, filename);
  };

  // CSV Import Handler
  const handleImport = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('locationId', 'default-location-id'); // TODO: Get from context

      const response = await api.post('/customers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        alert(response.data.message);
        // Reload page
        window.location.reload();
      } else {
        alert(`Import failed: ${response.data.error}`);
      }
    } catch (error) {
      console.error('Import error:', error);
      alert(`Import failed: ${error.response?.data?.error || error.message}`);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
          Customer Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ImportExportButtons
            onExport={handleExport}
            onImport={handleImport}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ fontWeight: 'bold' }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  {stat.icon}
                </Box>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.title}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <CardContent>
          {/* Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search customers by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Customers Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Segment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Orders</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Total Spent</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Avg Order</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Last Order</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCustomers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((customer) => (
                    <TableRow key={customer.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ mr: 2, backgroundColor: 'primary.main' }}>
                            {customer.name.charAt(0)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {customer.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {customer.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.company}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={customer.segment}
                          color={getSegmentColor(customer.segment)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {customer.totalOrders}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          ${customer.totalSpent.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          ${customer.averageOrderValue.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{customer.lastOrderDate}</Typography>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(e) => handleMenuOpen(e, customer)}
                          size="small"
                        >
                          <MoreIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCustomers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleDetailsOpen(selectedCustomer)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem>
          <EditIcon sx={{ mr: 1 }} />
          Edit Customer
        </MenuItem>
        <MenuItem>
          <EmailIcon sx={{ mr: 1 }} />
          Send Email
        </MenuItem>
        <MenuItem>
          <CallIcon sx={{ mr: 1 }} />
          Call Customer
        </MenuItem>
      </Menu>

      {/* Customer Details Dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Customer Details - {selectedCustomer?.name}</DialogTitle>
        <DialogContent>
          {selectedCustomer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Contact Information
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Customer ID</Typography>
                  <Typography>{selectedCustomer.id}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Name</Typography>
                  <Typography>{selectedCustomer.name}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography>{selectedCustomer.email}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Phone</Typography>
                  <Typography>{selectedCustomer.phone}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Company</Typography>
                  <Typography>{selectedCustomer.company}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Registration Date</Typography>
                  <Typography>{selectedCustomer.registrationDate}</Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Purchase History
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Segment</Typography>
                  <Chip
                    label={selectedCustomer.segment}
                    color={getSegmentColor(selectedCustomer.segment)}
                    size="small"
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>{selectedCustomer.totalOrders}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Total Spent</Typography>
                  <Typography sx={{ fontWeight: 'bold' }}>${selectedCustomer.totalSpent.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Average Order Value</Typography>
                  <Typography>${selectedCustomer.averageOrderValue.toLocaleString()}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">Last Order</Typography>
                  <Typography>{selectedCustomer.lastOrderDate}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Recent Orders
                </Typography>
                <List>
                  {selectedCustomer.orderHistory.map((order, index) => (
                    <React.Fragment key={index}>
                      <ListItem>
                        <ListItemText
                          primary={order.items}
                          secondary={`${order.date} - $${order.amount.toLocaleString()}`}
                        />
                      </ListItem>
                      {index < selectedCustomer.orderHistory.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Notes
                </Typography>
                <Typography>{selectedCustomer.notes}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
          <Button variant="contained">Edit Customer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Customers;
